import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

type Mode = 'light' | 'dark'

interface ThemeState {
  mode: Mode
  setMode: (m: Mode) => void
}

const ThemeContext = createContext<ThemeState | null>(null)

const MODE_KEY = 'mi-theme-mode'

function getInitialMode(): Mode {
  if (typeof window === 'undefined') return 'light'
  const saved = window.localStorage.getItem(MODE_KEY)
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Light / dark, per user, and nothing else.
 *
 * The palette, font and type scale are fixed in `src/index.css`. There used to
 * be a tenant-wide `appearance` setting (presets, a custom primary, font,
 * scale, radius) written onto :root from here; it was removed with the move to
 * the one brand palette, and an `appearance` row left in `app_settings` is
 * simply never read.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => getInitialMode())

  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark')
  }, [mode])

  const setMode = useCallback((m: Mode) => {
    window.localStorage.setItem(MODE_KEY, m)
    setModeState(m)
  }, [])

  return <ThemeContext.Provider value={{ mode, setMode }}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
